import { Box, Button, CircularProgress, Divider, Fade, Link, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import React, { useEffect, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { RadioButtonUnchecked } from '@mui/icons-material';
import CopyTextComponent from '../../../components/CopyTextComponent';
import { blue } from '@mui/material/colors';
import appConfig from '../../../config/appConfig';

const INITIAL_STEPS = [
  { id: 1, title: 'Verification with EmaraTax', sub: '', status: 'pending' },
  { id: 2, title: `Creating Peppol ID`, sub: 'Created and registered', status: 'pending' },
  {
    id: 3,
    title: 'Peppol ID updated in FTA directory',
    sub: '',
    status: 'pending',
  },
  {
    id: 4,
    title: 'ASP linking completed!',
    sub: `Email ${appConfig.brandEmail} to complete your onboarding and begin E-invoicing in the UAE.`,
    email: appConfig.brandEmail,
    status: 'pending',
  },
];

export default function OnboardingComplete(props) {
  const peppolId = `0235:${props?.verifiedProfileData?.TIN}`;

  const [steps, setSteps] = useState(INITIAL_STEPS);
  const [countdown, setCountdown] = useState(30);
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    const processStep = async () => {
      for (const step of INITIAL_STEPS) {
        updateStatus(step.id, 'loading');
        let loadTime = 2000;
        if (step.id === 1) {
          loadTime = 0;
        }
        await new Promise((resolve) => setTimeout(resolve, loadTime));
        updateStatus(step.id, 'success');
      }
      setIsFinished(true);
    };
    processStep();
  }, []);

  useEffect(() => {
    let timer;
    if (isFinished && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      handleRedirect();
    }
    return () => clearTimeout(timer);
  }, [isFinished, countdown]);

  const handleRedirect = () => {
    window.close();
  };

  const updateStatus = (id, newStatus) => {
    setSteps((prev) => prev.map((step) => (step.id === id ? { ...step, status: newStatus } : step)));
  };

  return (
    <Stack sx={{ justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
      <Stack direction="column" border={2} borderColor="grey.200" borderRadius={5} p={6} height="75%" width="25%">
        {isFinished ? (
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 4, textAlign: 'center' }}>
            All done!
          </Typography>
        ) : (
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 4, textAlign: 'center' }}>
            Linking in progress...
          </Typography>
        )}

        <Stack>
          {steps.map((step) => {
            const isPending = step.status === 'pending';
            const isLoading = step.status === 'loading';
            const isSuccess = step.status === 'success';

            return (
              <Stack key={step.id} direction="row" sx={{ mb: 2 }}>
                <Box sx={{ mr: 2 }}>
                  {isSuccess ? (
                    <Fade in>
                      <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
                    </Fade>
                  ) : isLoading ? (
                    <CircularProgress size={32} thickness={2} />
                  ) : (
                    <RadioButtonUnchecked overlineThickness={10} sx={{ fontSize: 32 }} />
                  )}
                </Box>
                <Box>
                  <Typography>{step.title}</Typography>
                  {isSuccess && (
                    <Fade in timeout={500}>
                      <Box>
                        <Stack>
                          <Typography color="text.secondary" variant="body2">
                            {step.email ? (
                              <span>
                                {step.sub.split(step.email)[0]}
                                <Link
                                  href={`mailto:${step.email}`}
                                  underline="hover"
                                  sx={{ color: blue[700], fontWeight: 'medium' }}
                                >
                                  {step.email}
                                </Link>
                                {step.sub.split(step.email)[1]}
                              </span>
                            ) : (
                              step.sub
                            )}
                          </Typography>
                        </Stack>
                        {step.id === 2 && (
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography color="text.secondary" variant="body2">
                              Peppol ID:
                            </Typography>
                            <Typography sx={{ color: blue[700] }}>
                              <CopyTextComponent copyText={peppolId} />
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    </Fade>
                  )}
                </Box>
              </Stack>
            );
          })}
          <Box sx={{ mt: 2 }}>
            {isFinished && (
              <Fade in timeout={800}>
                <Stack>
                  <Divider sx={{ mt: 2, mb: 2 }} />
                  <Typography variant="caption" color="textSecondary" textAlign="center" sx={{ mb: 1 }}>
                    Your linking is successful. Redirecting to EmaraTax in <strong> {countdown} seconds...</strong>
                  </Typography>
                  <Button variant="contained" size="large" onClick={handleRedirect} sx={{ borderRadius: 1, px: 4 }}>
                    Redirect To EmaraTax Now
                  </Button>
                </Stack>
              </Fade>
            )}
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
}
